import type { User, LoginRequest, RegisterRequest, Model, AnalysisResults } from '@printability/shared-types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

interface APIResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  timestamp: string;
}

class APIError extends Error {
  constructor(
    public code: string,
    message: string,
    public statusCode: number,
    public details?: any
  ) {
    super(message);
    this.name = 'APIError';
  }
}

class APIService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add auth token if available
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    try {
      const response = await fetch(url, config);
      const data: APIResponse<T> = await response.json();

      if (!data.success) {
        throw new APIError(
          data.error?.code || 'UNKNOWN_ERROR',
          data.error?.message || 'An unknown error occurred',
          response.status,
          data.error?.details
        );
      }

      return data.data as T;
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }

      // Network or parsing error
      throw new APIError(
        'NETWORK_ERROR',
        'Failed to connect to the API',
        0,
        error
      );
    }
  }

  // Health check
  async healthCheck(): Promise<{ status: string; version: string; environment: string }> {
    return this.request('/health');
  }

  // Authentication
  async login(credentials: LoginRequest): Promise<{ user: User; tokens: any }> {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async register(userData: RegisterRequest): Promise<{ user: User; tokens: any }> {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async logout(): Promise<{ message: string }> {
    return this.request('/auth/logout', {
      method: 'POST',
    });
  }

  // Models
  async getModels(params?: {
    page?: number;
    perPage?: number;
    status?: string;
  }): Promise<{
    data: Model[];
    pagination: {
      page: number;
      perPage: number;
      total: number;
      totalPages: number;
    };
  }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.perPage) searchParams.set('perPage', params.perPage.toString());
    if (params?.status) searchParams.set('status', params.status);

    const query = searchParams.toString();
    const endpoint = `/api/models${query ? `?${query}` : ''}`;

    const response = await this.request<Model[]>(endpoint);

    // The API returns the data directly, but we need to match our expected format
    return {
      data: response as any,
      pagination: {
        page: 1,
        perPage: 20,
        total: (response as any).length || 0,
        totalPages: 1,
      },
    };
  }

  async getModel(id: string): Promise<{ model: Model }> {
    return this.request(`/api/models/${id}`);
  }

  // Analysis
  async getAnalysisResults(id: string): Promise<AnalysisResults> {
    return this.request(`/api/analysis/${id}/results`);
  }

  // File Upload and Processing
  async uploadAndAnalyze(
    file: File,
    analysisType: 'basic' | 'advanced' | 'full' = 'advanced',
    _onProgress?: (progress: number) => void
  ): Promise<{
    analysisId: string;
    filename: string;
    analysisType: string;
    status: string;
    results: AnalysisResults;
    processingTime: number;
    createdAt: string;
    completedAt: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}/upload/analyze?type=${analysisType}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
      },
      body: formData,
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      // Handle our new error format for complex models
      if (result.errorType === 'MODEL_TOO_COMPLEX' && result.complexity) {
        const complexityInfo = result.complexity;
        const errorMessage = `${result.error}\n\nModel Details:\n• Estimated memory: ${complexityInfo.estimatedMemoryMB}MB\n• Complexity score: ${complexityInfo.complexityScore}/100\n• Risk level: ${complexityInfo.riskLevel}\n\nRecommendations:\n${complexityInfo.recommendations.map((r: string) => `• ${r}`).join('\n')}`;

        throw new APIError(
          result.errorType || 'MODEL_TOO_COMPLEX',
          errorMessage,
          response.status,
          result
        );
      }

      // Handle memory limit exceeded errors
      if (result.errorType === 'MEMORY_LIMIT_EXCEEDED') {
        const errorMessage = `${result.error}\n\n${result.details}\n\nSuggestions:\n${result.suggestions?.map((s: string) => `• ${s}`).join('\n') || ''}`;

        throw new APIError(
          result.errorType,
          errorMessage,
          response.status,
          result
        );
      }

      // Handle generic errors
      throw new APIError(
        result.errorType || 'UPLOAD_ERROR',
        result.error || result.details || `Upload failed: ${response.status}`,
        response.status,
        result
      );
    }

    return result.data;
  }

  async getSupportedFormats(): Promise<{
    extension: string;
    name: string;
    description: string;
  }[]> {
    const result = await this.request('/upload/formats') as { formats: { extension: string; name: string; description: string; }[] };
    return result.formats;
  }

  async estimateProcessingTime(fileSize: number): Promise<{
    estimatedTime: number;
    estimatedTimeSeconds: number;
  }> {
    return this.request(`/upload/estimate?size=${fileSize}`);
  }

  async startAnalysis(modelId: string, analysisType: 'basic' | 'advanced' | 'full'): Promise<{
    analysisId: string;
    status: string;
    estimatedTime: number;
  }> {
    return this.request('/api/analysis/start', {
      method: 'POST',
      body: JSON.stringify({ modelId, analysisType }),
    });
  }

  async getAnalysisStatus(id: string): Promise<{
    analysisId: string;
    status: string;
    progress: number;
    estimatedTimeRemaining: number;
  }> {
    return this.request(`/api/analysis/${id}/status`);
  }
}

export const apiService = new APIService();
export { APIError };
export type { APIResponse };
